/// <amd-module name="Feed/Legacy/Protocols/Ivy/IvyPackageProtocol" />
import * as React from "react";

import { IVssPageContext } from "VSS/Platform/Context";

import { FeedRole, FeedView, Package, PackageMetrics, PackageVersion, PackageVersionMetrics } from "Feed/Common/Contracts/Feed";
import { UpstreamingBehavior } from "Feed/Common/Protocols/Shared/Contracts/PackagingShared";
import * as PackageResources from "Feed/Common/Resources";
import { BowtieIconProps } from "Feed/Common/Utils/Icons";
import { ProtocolCommands } from "Feed/Legacy/Common/ProtocolCommands";
import * as PermissionHelper from "Feed/Legacy/Helpers/PermissionHelper";
import { DeleteCommandHelper } from "Feed/Legacy/Protocols/Common/DeleteCommandHelper";
import { IPackageDetailsProps } from "Feed/Legacy/Protocols/Common/IPackageDetailsProps";
import { IPackageProtocol, IPackageProtocolClient } from "Feed/Legacy/Protocols/Common/IPackageProtocol";
import { ISupportedFeatures } from "Feed/Legacy/Protocols/Common/ISupportedFeatures";
import { MinimalPackageDetails } from "Feed/Legacy/Protocols/Common/WebApi/PackagingShared.Contracts";
import { IvyPromoteCommand } from "Feed/Legacy/Protocols/Ivy/Commands/IvyPromoteCommand";
import { IvyCiConstants } from "Feed/Legacy/Protocols/Ivy/Constants/IvyConstants";
import { IvyClientTool } from "Feed/Legacy/Protocols/Ivy/IvyClientTool";
import { IvyOverviewAttributesPanel } from "Feed/Legacy/Protocols/Ivy/IvyOverviewAttributesPanel";
import { IvyOverviewContentPanel } from "Feed/Legacy/Protocols/Ivy/IvyOverviewContentPanel";
import { IPackageCommand } from "Feed/Legacy/Types/IPackageCommand";
import { Feed } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

import { IvyDeleteCommand } from "./Commands/IvyDeleteCommand";

export class IvyPackageProtocol implements IPackageProtocol {
    private static readonly _icon = new BowtieIconProps("brand-ivy");

    public readonly name = IvyCiConstants.ProtocolName;
    public readonly key = IvyCiConstants.ProtocolName.toLowerCase();
    get vssIconProps() {
        return IvyPackageProtocol._icon;
    }
    public supportedCommandsMask: ProtocolCommands = ProtocolCommands.Noop;
    public readonly clients: IPackageProtocolClient[] = [];
    private pageContext: IVssPageContext;

    constructor(pageContext: IVssPageContext) {
        this.pageContext = pageContext;

        this.clients.push(new IvyClientTool(this.pageContext));
    }

    public getPackageMessage(packageSummary: Package, packageVersion: PackageVersion): string {
        return null;
    }

    public getOverviewAttributesPanel(
        feed: Feed,
        packageSummary: Package,
        packageVersion: PackageVersion,
        packageVersionMetrics: PackageVersionMetrics,
        packageMetrics: PackageMetrics,
        isSmartDependenciesEnabled: boolean,
        isProvenanceEnabled: boolean,
        isMetricsEnabled: boolean,
        isMetricsLoading: boolean
    ): React.ReactNode {
        return React.createElement(IvyOverviewAttributesPanel, {
            pageContext: this.pageContext,
            feed,
            packageSummary,
            packageVersion,
            protocol: this,
            packageVersionMetrics,
            packageMetrics,
            isSmartDependenciesEnabled,
            isProvenanceEnabled,
            isMetricsEnabled,
            isMetricsLoading
        } as IPackageDetailsProps);
    }

    public getOverviewContentPanel(
        feed: Feed,
        feedViews: FeedView[],
        packageSummary: Package,
        packageVersion: PackageVersion
    ): React.ReactNode {
        return React.createElement(IvyOverviewContentPanel, {
            pageContext: this.pageContext,
            feed,
            feedViews,
            packageSummary,
            packageVersion,
            protocol: this
        } as IPackageDetailsProps);
    }

    public getMultiSelectPackageCommands(
        feed: Feed,
        selectedPackages?: Package[],
        selectedVersions?: PackageVersion[]
    ): IPackageCommand[] {
        return null;
    }

    public getPackageCommands(
        packageSummary: Package,
        packageVersion: PackageVersion,
        feed: Feed,
        viaPackageList: boolean = false
    ): IPackageCommand[] {
        const role: FeedRole = PermissionHelper.getUsersRoleForFromFeed(feed);
        const packageCommands = new Array<IPackageCommand>();

        if (!packageVersion.isDeleted && !packageSummary.isCached && role) {
            const isAdmin = PermissionHelper.isAdministratorFromRole(role);
            const isContributor = PermissionHelper.isContributor(role);

            if (isAdmin || isContributor) {
                packageCommands.push(
                    new IvyPromoteCommand(this.pageContext, packageSummary, packageVersion, feed, viaPackageList)
                );
            }

            const selectedPackages: Package[] = viaPackageList ? [packageSummary] : null;
            const selectedVersions: PackageVersion[] = viaPackageList ? null : [packageVersion];
            const deleteCommands = DeleteCommandHelper.addDeleteCommands(
                IvyCiConstants.ProtocolName,
                selectedPackages,
                selectedVersions,
                packageSummary.name,
                !isAdmin /*disabled*/
            );
            packageCommands.push(...deleteCommands);
        }

        return packageCommands;
    }

    public supportedFeatures(feed?: Feed): ISupportedFeatures {
        const features: ISupportedFeatures = {
            upstreams: false,
            projectScopedFeeds: false
        };
        return features;
    }

    // Use supportedFeatures instead.
    // Except for connect to feed dialog that is located in the old Feed/Service/Web location.
    public supportsUpstreams(feed?: Feed): boolean {
        return false;
    }

    public async deleteLatestVersions(
        projectId: string = null /*Project scoped feeds not yet enabled for this protocol */,
        feed: Feed,
        pkgs: Package[]
    ): Promise<void> {
        try {
            const packageToDelete = pkgs[0];
            const packageVersion = packageToDelete.versions[0];
            await IvyDeleteCommand.onDeletePackage(this.pageContext, packageToDelete, packageVersion, feed, false);
        } catch (error) {
            throw new Error(this.key);
        }
    }

    public async deletePackageVersions(
        projectId: string = null /*Project scoped feeds not yet enabled for this protocol */,
        feed: Feed,
        pkg: Package,
        versions: PackageVersion[]
    ) {
        try {
            const packageToDelete = versions[0];
            await IvyDeleteCommand.onDeletePackage(this.pageContext, pkg, packageToDelete, feed, false);
        } catch (error) {
            throw new Error(this.key);
        }
    }

    public async restorePackageVersionsToFeed(
        projectId: string = null /*Project scoped feeds not yet enabled for this protocol */,
        feed: Feed,
        pkg: Package,
        versions: PackageVersion[]
    ) {
        throw new Error(PackageResources.PackageRestoreNotSupportedOnThisProtocol);
    }

    public permanentlyDeletePackageVersions(
        projectId: string,
        feed: Feed,
        pkg: Package,
        versions: PackageVersion[]
    ): Promise<void>;
    public permanentlyDeletePackageVersions(
        projectId: string,
        feed: Feed,
        packageVersions: MinimalPackageDetails[]
    ): Promise<void>;
    public permanentlyDeletePackageVersions(
        projectId: string,
        feed: Feed,
        pkgOrDetails: Package | MinimalPackageDetails[],
        versions?: PackageVersion[]
    ): Promise<void> {
        throw new Error(PackageResources.PermanentDeleteNotSupportedOnThisProtocol);
    }

    public promotePackageVersions(
        projectId: string,
        feed: Feed,
        feedView: FeedView,
        pkg: Package,
        versions: PackageVersion[]
    ): Promise<void>;
    public promotePackageVersions(
        projectId: string,
        feed: Feed,
        feedView: FeedView,
        packageVersions: MinimalPackageDetails[]
    ): Promise<void>;
    public promotePackageVersions(
        projectId: string,
        feed: Feed,
        feedView: FeedView,
        pkgOrDetails: Package | MinimalPackageDetails[],
        versions?: PackageVersion[]
    ): Promise<void> {
        throw new Error("Method not implemented.");
    }

    public getCopyInstallCommand(feed: Feed, pkgName: string, pkgVersion: PackageVersion, separator?: string): string {
        return null;
    }

    public getUpstreamingBehavior(feed: Feed, pkg: Package): Promise<UpstreamingBehavior> {
        throw new Error(PackageResources.UpstreamBehaviorNotSupportedOnThisProtocol);
    }

    public setUpstreamingBehavior(feed: Feed, pkg: Package, behavior: UpstreamingBehavior): Promise<void> {
        throw new Error(PackageResources.UpstreamBehaviorNotSupportedOnThisProtocol);
    }
}
